import React, { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ShieldCheck, Activity, LockKeyhole, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { dragRegionStyle, noDragRegionStyle, isDesktop } from "@/lib/desktopShell";

/**
 * Login — operator sign-in for MoovBytes' control plane.
 *
 * Same demo credentials + same hero background as the previous design.
 * The refresh swaps a 3-column split for a left-weighted hero with a
 * floating glass-morphism card, adds grain + grid overlays to give the
 * dark navy some texture, and restyles the form with a softer card,
 * mono micro-label column, and a full-width gradient CTA.
 *
 * Logo swapped from the colored-on-white chip to the white-on-transparent
 * mark — reads correctly on the dark navy without any background wrapper.
 */
export default function Login() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [totpCode, setTotpCode] = useState("");
    const [needsTotp, setNeedsTotp] = useState(false);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    if (user) return <Navigate to={location.state?.from || "/"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErr("");
        try {
            const r = await login(email, password, needsTotp ? totpCode : null);
            if (r?.requires_totp) {
                setNeedsTotp(true);
                setErr("");
            }
        } catch (e) {
            setErr(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className={
                isDesktop()
                    // Desktop's window is fixed-size and not meant to scroll on the
                    // login screen — h-screen + overflow-hidden (vs. min-h-screen)
                    // keeps Chromium from showing a permanent vertical scrollbar
                    // track down the right edge when the layout is a hair taller
                    // than the viewport.
                    ? "relative h-screen overflow-hidden bg-[#03122b] text-white"
                    : "relative min-h-screen overflow-hidden bg-[#03122b] text-white"
            }
            data-testid="login-page"
        >
            {/* Background layer — unchanged photo + gradient from the previous design */}
            <div
                className="absolute inset-0 bg-cover bg-center opacity-30"
                style={{
                    backgroundImage:
                        "url(https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByYWNrJTIwYWJzdHJhY3QlMjBkYXRhJTIwY2VudGVyfGVufDB8fHx8MTc3NjczNzU0N3ww&ixlib=rb-4.1.0&q=85)",
                }}
            />
            <div className="absolute inset-0 grid-bg opacity-[0.18]" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#03122b] via-[#03122b]/88 to-[#004AAD]/25" />
            {/* Radial accent glow */}
            <div className="pointer-events-none absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-[#00C2FF]/15 blur-[140px]" />
            <div className="pointer-events-none absolute -right-40 bottom-0 h-[440px] w-[440px] rounded-full bg-[#5b5ef0]/15 blur-[140px]" />

            {/* Top bar: logo + small help link */}
            <header className="relative z-10 flex items-center justify-between px-6 pt-6 sm:px-12 sm:pt-8" style={dragRegionStyle}>
                <Link to="/" data-testid="login-logo-link" aria-label="Back to home" style={noDragRegionStyle}>
                    <img
                        src="/brand/moovbytes-logo-white.png"
                        alt="MoovBytes"
                        className="h-[38.4px] w-auto select-none transition hover:opacity-80"
                        data-testid="login-logo"
                    />
                </Link>
                {!isDesktop() && (
                    <Link
                        to="/register"
                        className="group hidden items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white/60 transition hover:text-white sm:inline-flex"
                        style={noDragRegionStyle}
                    >
                        New operator?{" "}
                        <span className="text-white underline decoration-[#00C2FF] decoration-2 underline-offset-4">
                            Create account
                        </span>
                        <ArrowRight size={12} className="translate-x-0 transition group-hover:translate-x-0.5" />
                    </Link>
                )}
            </header>

            {/* Main layout — hero + glass card in the browser app; the
                Desktop app is already installed and licensed by the time
                anyone sees this screen, so the marketing pitch (hero copy,
                feature strip, "New operator?" CTA above) is dead weight
                there — just the sign-in card, centered. */}
            <main
                className={
                    isDesktop()
                        ? "relative z-10 mx-auto flex min-h-[calc(100vh-88px)] max-w-7xl items-center justify-center px-6 pb-10 pt-6 sm:px-12"
                        : "relative z-10 mx-auto grid min-h-[calc(100vh-88px)] max-w-7xl items-center gap-10 px-6 pb-10 pt-6 sm:px-12 md:grid-cols-[1.25fr_1fr] md:gap-14 md:pt-10"
                }
            >
                {/* Left — hero copy (browser app only) */}
                {!isDesktop() && (
                    <section className="max-w-xl">
                        <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
                            Move petabytes.<br />
                            Prove integrity.<br />
                            <span className="bg-gradient-to-r from-white via-[#cfe9ff] to-[#00C2FF] bg-clip-text text-transparent">
                                Sleep at night.
                            </span>
                        </h1>
                        <p className="mt-6 max-w-md text-[15px] leading-relaxed text-white/70">
                            Agent-based data migration with SHA-256 verification, AES-256 in-transit
                            encryption, block-level delta sync, and SOC2-ready compliance reporting
                            — directly between your servers. <span className="font-semibold text-white">Never through us.</span>
                        </p>

                        {/* Feature strip — same 3 items, new glass-morphism chips */}
                        <div className="mt-10 grid w-full max-w-md grid-cols-3 gap-3">
                            {[
                                { k: "AES-256", v: "In transit", icon: LockKeyhole },
                                { k: "SHA-256", v: "Integrity", icon: ShieldCheck },
                                { k: "P2P", v: "Agents", icon: Activity },
                            ].map((s) => (
                                <div
                                    key={s.k}
                                    className="group rounded-xl border border-white/10 bg-white/[0.03] p-3 backdrop-blur-sm transition hover:border-[#00C2FF]/40 hover:bg-white/[0.06]"
                                >
                                    <s.icon size={16} className="text-[#00C2FF] transition group-hover:scale-110" />
                                    <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/55">
                                        {s.v}
                                    </div>
                                    <div className="mt-0.5 font-display text-base font-bold tabular-nums">
                                        {s.k}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Sign-in glass card */}
                <section className={isDesktop() ? "w-full max-w-md" : "w-full md:justify-self-end"}>
                    <div
                        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-8"
                        data-testid="login-card"
                    >
                        {/* Subtle top gradient accent */}
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/60 to-transparent" />

                        <div className="mb-7">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00C2FF]">
                                Operator sign-in
                            </div>
                            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-white">
                                Welcome back
                            </h2>
                            <p className="mt-1 text-sm text-white/55">
                                Continue to your control plane.
                            </p>
                        </div>

                        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                            <Field label="Email">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="login-email-input"
                                    className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-[#00C2FF]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#00C2FF]/20"
                                />
                            </Field>

                            <Field label="Password">
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="login-password-input"
                                    className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-[#00C2FF]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#00C2FF]/20"
                                />
                            </Field>

                            <div className="-mt-1 text-right">
                                <Link
                                    to="/forgot-password"
                                    data-testid="link-forgot-password"
                                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55 transition hover:text-[#00C2FF]"
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            {/* TOTP prompt — only rendered after the backend
                                signals `requires_totp:true`. Keeps the form
                                simple for the 95%+ of users without 2FA. */}
                            {needsTotp && (
                                <Field label="2-factor code">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={totpCode}
                                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        placeholder="123456"
                                        autoFocus
                                        required
                                        data-testid="login-totp-input"
                                        className="w-full rounded-lg border border-[#00C2FF]/40 bg-white/[0.04] px-3 py-2.5 text-center font-mono text-base tracking-[0.4em] text-white placeholder:text-white/30 outline-none transition focus:border-[#00C2FF]/80 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#00C2FF]/30"
                                    />
                                    <p className="mt-1 text-[10px] text-white/45">
                                        Open your authenticator app and enter the current 6-digit code.
                                    </p>
                                </Field>
                            )}

                            {err && (
                                <div
                                    data-testid="login-error"
                                    className="rounded-lg border border-[#ef4444]/40 bg-[#ef4444]/10 px-3 py-2 text-xs text-[#fca5a5]"
                                >
                                    {err}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="login-submit-btn"
                                className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#5b5ef0] px-3 py-3 text-sm font-semibold uppercase tracking-widest text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110 hover:shadow-xl hover:shadow-[#00C2FF]/25 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading ? "Signing in…" : "Sign in"}
                                {!loading && (
                                    <ArrowRight
                                        size={15}
                                        className="translate-x-0 transition group-hover:translate-x-0.5"
                                    />
                                )}
                            </button>
                        </form>

                        {/* SAML SSO entry — prompts for a tenant slug and
                            redirects to the per-tenant SP-initiated login URL. */}
                        <div className="mt-5 flex items-center gap-3 text-[10px] uppercase tracking-widest text-white/35">
                            <div className="h-px flex-1 bg-white/10" />
                            Or
                            <div className="h-px flex-1 bg-white/10" />
                        </div>
                        <button
                            type="button"
                            data-testid="login-sso-btn"
                            onClick={() => {
                                const slug = window.prompt("Enter your MoovBytes tenant slug to start SSO:", "");
                                if (slug && slug.trim()) {
                                    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                                    window.location.href = `${apiBase}/api/auth/saml/${encodeURIComponent(slug.trim())}/login`;
                                }
                            }}
                            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-white/85 transition hover:border-[#00C2FF]/50 hover:bg-white/[0.08]"
                        >
                            Sign in with SSO (SAML)
                        </button>

                        {/* Desktop is already installed and licensed by the time anyone
                            sees this screen (same reasoning as the hero/feature-strip
                            gating above) — new-account signup is a browser-only path. */}
                        {!isDesktop() && (
                            <div className="mt-6 text-xs text-white/55">
                                Don&rsquo;t have an account?{" "}
                                <Link
                                    to="/register"
                                    data-testid="link-register"
                                    className="font-semibold text-white underline decoration-[#00C2FF] decoration-2 underline-offset-4 hover:text-[#00C2FF]"
                                >
                                    Create one
                                </Link>
                            </div>
                        )}
                    </div>
                </section>
            </main>

            {/* Footer micro-strip */}
            <footer className="relative z-10 flex items-center justify-between px-6 pb-6 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 sm:px-12 sm:pb-8">
                <div>© MoovBytes · Control plane</div>
                <div className="hidden items-center gap-2 sm:flex">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#10b981]" />
                    All systems operational
                </div>
            </footer>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-white/45">
                {label}
            </span>
            {children}
        </label>
    );
}
